<template>
  <div
    v-if="wizardSteps.length > 0"
    class="canvas-completion-wizard fixed inset-0 z-100 flex"
    :style="{ backgroundColor: themeVars.base }"
    @keydown="handleKeydown"
  >
    <div
      class="wizard-sidebar flex flex-col transition-all duration-300"
      :class="isCollapsed ? 'w-16' : 'w-1/4 max-w-[300px]'"
      :style="{ borderRight: `1px solid ${themeVars.primary5}` }"
    >
      <div v-if="!isCollapsed" class="wizard-header p-4 pb-2">
        <h2 class="text-xl font-bold mb-2" :style="{ color: themeVars.text }">
          {{ t('canvasUI.completionWizard.title') }}
        </h2>
        <p class="text-sm mt-2 mb-2" :style="{ color: themeVars.text2 }">
          {{ t('canvasUI.completionWizard.subtitle', { c: activeCanvas?.name }) }}
        </p>
      </div>

      <div class="wizard-steps flex-1 overflow-y-auto" :class="isCollapsed ? 'p-2' : 'px-4'">
        <div
          v-for="(step, index) in wizardSteps"
          :key="step.id"
          v-tooltip="isCollapsed ? step.name || `${t('canvasUI.completionWizard.step')} ${index + 1}` : undefined"
          class="wizard-step mb-2 rounded-md cursor-pointer flex items-center transition-all"
          :class="isCollapsed ? 'p-2 justify-center' : 'p-3'"
          :style="{
            backgroundColor: currentStepIndex === index ? themeVars.primary5 : 'transparent',
            border: currentStepIndex === index ? `1px solid ${themeVars.primary4}` : '1px solid transparent',
            color: completedSteps[step.id] ? themeVars.success : themeVars.text,
          }"
          @click="setCurrentStep(index)"
        >
          <div
            class="step-number flex items-center justify-center w-6 h-6 rounded-full min-w-6"
            :class="isCollapsed ? '' : 'mr-3'"
            :style="{
              backgroundColor: completedSteps[step.id] ? themeVars.success : themeVars.primary,
              color: themeVars.base,
            }"
          >
            <CIcon v-if="completedSteps[step.id]" icon="check" size="12px" />
            <span v-else>{{ index + 1 }}</span>
          </div>
          <div v-if="!isCollapsed" class="step-info">
            <div class="step-name font-medium">
              {{ step.name || `${t('canvasUI.completionWizard.step')} ${index + 1}` }}
            </div>
            <div class="step-type text-xs hidden" :style="{ color: themeVars.text2 }">
              {{ step.appDescription || getStrategyLabel(step.strategy) }}
            </div>
          </div>
        </div>
      </div>

      <div class="wizard-sidebar-footer p-4 flex justify-center">
        <CButton
          v-tooltip="isCollapsed ? t('canvasUI.completionWizard.expand') : t('canvasUI.completionWizard.collapse')"
          circle
          @click="toggleSidebar"
        >
          <template #icon>
            <CIcon :icon="isCollapsed ? 'arrow-right-to-line' : 'arrow-left-to-line'" size="16px" />
          </template>
        </CButton>
      </div>
    </div>

    <div class="wizard-content flex-1 flex flex-col">
      <div v-if="currentStep" class="wizard-step-content flex-1 flex">
        <!-- For free strategy -->
        <template
          v-if="
            mounted &&
            !completedSteps[currentStep.id] &&
            currentStep.strategy &&
            ['free', 'at-least-one', 'single'].includes(currentStep.strategy) &&
            !currentStep.is_restricted_to_preselections
          "
        >
          <SectionSelector
            v-if="!launchDarkly.enable_selectors_dnd"
            :key="currentStep.id"
            :cancel-text="t('canvasUI.common.skip')"
            :done-text="t('canvasUI.common.ok')"
            :is-saving-canvas="false"
            :selected-section-ids="currentStep.selectedSectionIds"
            :selection-strategy="currentStep.strategy"
            @cancel="handleSectionSelectorCancel"
            @select="handleSectionSelect"
          />
          <SectionSelectorWithDragAndDrop
            v-else
            :key="currentStep.id + 'drag-n-drop'"
            :cancel-text="t('canvasUI.common.skip')"
            :done-text="t('canvasUI.common.ok')"
            :is-saving-canvas="false"
            :selected-section-ids="currentStep.selectedSectionIds"
            :selection-strategy="currentStep.strategy"
            @cancel="handleSectionSelectorCancel"
            @select="handleSectionSelect"
          />
        </template>

        <!-- For admin-selected or exactly-one strategies -->
        <RestrictedSectionSelector
          v-else-if="
            currentStep.is_restricted_to_preselections &&
            (currentStep.strategy === 'at-least-one' || currentStep.strategy === 'single')
          "
          :key="currentStep.id + 'restricted-selections'"
          :preselected-ids="currentStep.selectedSectionIds"
          :section-list-data="getSectionListData(currentStep)"
          :selection-strategy="currentStep.strategy"
          @cancel="handleSectionSelectorCancel"
          @confirm="handleWizardRestrictedSectionSelect"
        />

        <!-- For custom app strategies -->
        <iframe
          v-else-if="currentStep.appSrc"
          :key="currentStep.id"
          ref="iframeRef"
          class="w-full h-full border-none"
          :src="currentStep.appSrc"
        />

        <!-- Completed step view with option to reopen -->
        <div v-else-if="completedSteps[currentStep.id]" class="flex flex-col items-center justify-center w-full h-full">
          <CErrorFullScreen
            class="pa-10 rounded-2"
            :description="t('canvasUI.completionWizard.completedStepDescription')"
            fit-parent
            icon="check-circle"
            :title="t('canvasUI.completionWizard.completedStepTitle')"
          >
            <CButton type="primary" @click="reopenStep">
              {{ t('canvasUI.completionWizard.editStep') }}
            </CButton>
          </CErrorFullScreen>
        </div>
      </div>
      <div class="wizard-actions flex justify-between w-full px-6 py-4">
        <CButton :disabled="isSaving" @click="cancel">{{ t('canvasUI.common.cancel') }}</CButton>
        <div class="flex gap-2">
          <CButton
            v-if="pitcherSettings?.canvas?.enable_wizard_preview_button"
            type="primary"
            @click="appEmit('previewCanvas', activeCanvas?.id)"
          >
            {{ t('canvasUI.completionWizard.preview') }}
          </CButton>
          <CButton
            :disabled="!allStepsCompleted && !isEditWizard"
            :loading="isSaving"
            type="primary"
            @click="completeWizard"
          >
            {{ t('canvasUI.completionWizard.complete') }}
          </CButton>
        </div>
      </div>
    </div>

    <!-- Cancel Confirmation Modal -->
    <CModal v-model:show="showCancelConfirmation" :z-index="1200" @update:show="showCancelConfirmation = $event">
      <CCard class="pa-6 max-w-[500px]">
        <div class="mb-4">
          <h3 class="text-lg font-semibold" :style="{ color: themeVars.text }">
            {{ t('canvasUI.completionWizard.cancelConfirmTitle') }}
          </h3>
        </div>
        <div class="mb-6" :style="{ color: themeVars.text2 }">
          {{ t('canvasUI.completionWizard.cancelConfirmMessage') }}
        </div>
        <div class="flex justify-end gap-2">
          <CButton @click="cancelDialogClose">
            {{ t('canvasUI.common.no') }}
          </CButton>
          <CButton type="primary" @click="confirmCancel">
            {{ t('canvasUI.common.yes') }}
          </CButton>
        </div>
      </CCard>
    </CModal>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, inject, nextTick, type ComputedRef, onBeforeUnmount } from 'vue'
import { useI18n } from 'vue-i18n'
import { cloneDeep, merge } from 'lodash-es'

import CButton from '@lib/components/CButton/CButton.vue'
import CIcon from '@lib/components/CIcon/CIcon.vue'
import CModal from '@lib/components/CModal/CModal.vue'
import CCard from '@lib/components/CCard/CCard.vue'
import useCanvas from '@canvas-builder/composables/useCanvas'
import useThemeVars from '@lib/composables/useThemeVars'
import { waitForIframeInitialize } from '@lib/utils/iframeInitialize'
import type { ComponentNode, ContentListSection, SectionListProps } from '@canvas-builder/types/canvas'
import type { CanvasRetrieve } from '@openapi'
import type { LaunchDarklyEnv } from '@lib/types/launchDarkly.types'
import SectionSelector from '@canvas-builder/components/ui/SectionList/SectionSelector.vue'
import SectionSelectorWithDragAndDrop from '@canvas-builder/components/ui/SectionList/SectionSelectorWithDragAndDrop.vue'
import RestrictedSectionSelector from '@canvas-builder/components/ui/SectionList/RestrictedSectionSelector.vue'
import useAppEmit from '@lib/composables/appEmit.use'
import CErrorFullScreen from '@lib/components/CError/CErrorFullScreen.vue'
import useCanvasCompletionWizard from '@canvas-builder/composables/useCanvasCompletionWizard'
import type { OrganizationSettings } from '@lib/types/organizationSettings.types'
import type { InstanceSettings } from '@lib/types/instanceSettings.types'

export interface WizardStep {
  id: string
  name?: string
  strategy: SectionListProps['selection_strategy']
  strategyLabel?: string
  appDescription?: string
  appSrc?: string | null
  selectedSectionIds: string[]
  componentNode: ComponentNode
  is_restricted_to_preselections?: boolean
}

const { appEmit } = useAppEmit()
const { t } = useI18n()
const themeVars = useThemeVars()
const iframeRef = ref<HTMLIFrameElement | null>(null)
const currentStepIndex = ref(0)
const mounted = ref(false)
const isSaving = ref(false)
const completedSteps = ref<Record<string, boolean>>({})
const isCollapsed = ref(false)
const showCancelConfirmation = ref(false)

const launchDarkly = inject<ComputedRef<LaunchDarklyEnv>>(
  'launchDarkly',
  computed(() => ({})),
)

const pitcherSettings = inject<ComputedRef<OrganizationSettings & InstanceSettings>>(
  'pitcherSettings',
  computed(() => ({})),
)

const { activeCanvas, saveCanvasContentWithContext, updateNodeDataById, onWizardEnter, onWizardExit } = useCanvas()
const { wizardSteps, getStrategyLabel, sectionListNodes } = useCanvasCompletionWizard()

const WIZARD_SIDEBAR_COLLAPSED_KEY = 'pitcher-wizard-sidebar-collapsed'

async function saveAll(ctx?: Record<string, any>) {
  isSaving.value = true
  await saveCanvasContentWithContext(ctx, { showToast: false }).catch((e) => {
    console.warn('[CanvasCompletionWizard] Failed to save canvas content with context', e)
  })
  isSaving.value = false
}

async function save(ctx: Record<string, any>, meta: Record<string, any>) {
  isSaving.value = true
  await saveCanvasContentWithContext(ctx, { showToast: false }, meta).catch((e) => {
    console.warn('[CanvasCompletionWizard] Failed to save wizard state', e)
  })
  isSaving.value = false
}

const toggleSidebar = () => {
  isCollapsed.value = !isCollapsed.value
  localStorage.setItem(WIZARD_SIDEBAR_COLLAPSED_KEY, String(isCollapsed.value))
}

const isEditWizard = computed(() => {
  return activeCanvas.value?.context?.pitcher?.completion_wizard?.edited
})

// Find all SectionList components with non-free selection strategies

const currentStep = computed<WizardStep | null>(() =>
  wizardSteps.value.length > currentStepIndex.value ? wizardSteps.value[currentStepIndex.value] : null,
)

// Initialize completed steps from persisted context per step with fallback to existing logic
const initializeCompletedSteps = () => {
  const newCompletedSteps: Record<string, boolean> = {}
  const persistedByIndex = (activeCanvas.value?.context as any)?.pitcher?.completion_wizard?.completed_steps as
    | Record<string, boolean>
    | undefined

  wizardSteps.value.forEach((step, index) => {
    const persisted = persistedByIndex?.[String(index)]
    if (persisted !== undefined) {
      newCompletedSteps[step.id] = !!persisted
      return
    }

    if (isEditWizard.value) {
      const hasSelectedSections = step.selectedSectionIds.length > 0
      let isCompleted = false
      if (step.strategy === 'single') isCompleted = step.selectedSectionIds.length === 1
      else if (step.strategy === 'at-least-one') isCompleted = step.selectedSectionIds.length >= 1
      else if (step.strategy === 'app') isCompleted = true
      else isCompleted = hasSelectedSections
      newCompletedSteps[step.id] = isCompleted
    } else {
      newCompletedSteps[step.id] = false
    }
  })

  completedSteps.value = newCompletedSteps

  const firstIncompleteIndex = wizardSteps.value.findIndex((step) => !completedSteps.value[step.id])
  if (firstIncompleteIndex >= 0) currentStepIndex.value = firstIncompleteIndex
}

const allStepsCompleted = computed(
  () => wizardSteps.value.length > 0 && wizardSteps.value.every((step) => completedSteps.value[step.id]),
)

async function setCurrentStep(index: number) {
  const canNavigate = await canNavigateAway()
  if (!canNavigate || isSaving.value) return

  currentStepIndex.value = index
  initializeCurrentStep()
}

function reopenStep() {
  if (!currentStep.value) return
  // Mark the current step as incomplete to reopen the selector
  completedSteps.value[currentStep.value.id] = false
  // Reinitialize the current step to show the selector again
  initializeCurrentStep()
}

async function initializeCurrentStep() {
  if (!currentStep.value) return
  else if (currentStep.value.appSrc) {
    // Wait for iframe to be mounted
    await nextTick()

    // Initialize the app iframe
    waitForIframeInitialize(
      iframeRef.value,
      {
        ...currentStep.value?.componentNode.data,
        canvas_id: activeCanvas?.value?.id,
        section_list_id: currentStep.value?.componentNode.id,
        initial_section_ids: currentStep.value?.componentNode.id
          ? (
              activeCanvas.value?.context?.pitcher?.completion_wizard?.initial_section_list_data?.[
                currentStep.value?.componentNode.id
              ] as SectionListProps['sections'] | undefined
            )?.map((section) => section.id)
          : undefined,
      },
      'section-selector',
      async (selectedData?: { section_ids?: string[] | ContentListSection[]; form_data?: Record<string, any> }) => {
        console.info(
          `[CanvasCompletionWizard]: Received done callback for ${currentStep.value?.componentNode.id} with data`,
          selectedData,
        )
        if (!selectedData) return
        handleAppSectionSelect(selectedData)
        if (selectedData.form_data) {
          const currentContext = cloneDeep(activeCanvas.value?.context || {})
          const existingMap =
            (currentContext?.pitcher?.completion_wizard?.completed_steps as Record<string, boolean> | undefined) ||
            ({} as Record<string, boolean>)
          const updatedCompletedMap: Record<string, boolean> = {
            ...existingMap,
            [String(currentStepIndex.value)]: true,
          }
          const payload: Record<string, any> = {
            pitcher: {
              completion_wizard: {
                ...currentContext?.pitcher?.completion_wizard,
                completed_steps: updatedCompletedMap,
              },
            },
          }
          payload.wizard = { ...currentContext?.wizard, ...selectedData.form_data }
          const updatedContext = {
            ...currentContext,
            pitcher: {
              ...(currentContext.pitcher ?? {}),
              completion_wizard: {
                ...(currentContext.pitcher?.completion_wizard ?? {}),
                ...payload.pitcher.completion_wizard,
              },
            },
            wizard: {
              ...(currentContext.wizard ?? {}),
              ...payload.wizard,
            },
          }
          await saveAll(updatedContext)
        }
        goToNextStep()
      },
      { componentName: 'CanvasCompletionWizard' },
    )
  }
}

function handleSectionSelect(sections: CanvasRetrieve[]) {
  if (!currentStep.value) return

  const sectionIds = sections.map((section) => section.id)
  const updatedSections = sectionIds.map((id) => ({ id }))

  const updatedData = {
    ...currentStep.value.componentNode.data,
    sections: updatedSections,
  }

  updateNodeDataById(currentStep.value.id, updatedData)

  completedSteps.value[currentStep.value.id] = true

  // Move to next step if available
  if (currentStepIndex.value < wizardSteps.value.length - 1) {
    currentStepIndex.value++
    initializeCurrentStep()
  }
}

const isStringArray = (items: string[] | ContentListSection[]): items is string[] => typeof items?.[0] === 'string'

function handleAppSectionSelect(selectedData: { section_ids?: string[] | ContentListSection[] }) {
  if (!currentStep.value || !selectedData.section_ids) return

  const items = selectedData.section_ids

  let updatedSections: { id: string }[] | ContentListSection[] = []

  if (isStringArray(items)) {
    updatedSections = items.map((id) => ({ id }))
  } else {
    updatedSections = items.map(({ id, type, component_id, page_index, file_id, sequence }) => ({
      id,
      type,
      component_id,
      page_index,
      file_id,
      sequence,
    }))
  }

  const updatedData = {
    ...currentStep.value.componentNode.data,
    sections: updatedSections,
  }

  updateNodeDataById(currentStep.value.id, updatedData)

  completedSteps.value[currentStep.value.id] = true
}

function goToNextStep() {
  // Move to next step if available
  if (currentStepIndex.value < wizardSteps.value.length - 1) {
    currentStepIndex.value++
    initializeCurrentStep()
  }
}

function handleSectionSelectorCancel() {
  if (!currentStep.value) return

  // Mark the current step as completed
  completedSteps.value[currentStep.value.id] = true

  // Keep existing selections if any, but mark as completed
  const updatedData = {
    ...currentStep.value.componentNode.data,
    sections: currentStep.value.selectedSectionIds.map((id) => ({ id })),
  }

  // Update the node data
  updateNodeDataById(currentStep.value.id, updatedData)

  // Move to next step if available
  if (currentStepIndex.value < wizardSteps.value.length - 1) {
    currentStepIndex.value++
    initializeCurrentStep()
  }
}

function handleWizardRestrictedSectionSelect(sectionIds: string[]) {
  if (!currentStep.value) return

  const updatedSections = sectionIds.map((id) => ({ id }))

  const updatedData = {
    ...currentStep.value.componentNode.data,
    sections: updatedSections,
  }

  updateNodeDataById(currentStep.value.id, updatedData)

  completedSteps.value[currentStep.value.id] = true

  // Move to next step if available
  if (currentStepIndex.value < wizardSteps.value.length - 1) {
    currentStepIndex.value++
    initializeCurrentStep()
  }
}

function getSectionListData(step: WizardStep): SectionListProps {
  return step.componentNode.data as SectionListProps
}

async function goToEditTriggeredStepIfNeeded() {
  const sectionListId = activeCanvas.value?.context?.pitcher?.completion_wizard?.edit_triggered_by_section_list_id
  if (!sectionListId) {
    await initializeCurrentStep()
    return
  }

  const targetIndex = wizardSteps.value.findIndex((step) => step.id === sectionListId)
  if (targetIndex >= 0) {
    currentStepIndex.value = targetIndex
    await initializeCurrentStep()

    // Clean up the edit trigger flag from context
    try {
      const currentContext = cloneDeep(activeCanvas.value?.context || {})
      if (currentContext?.pitcher?.completion_wizard?.edit_triggered_by_section_list_id) {
        delete currentContext.pitcher.completion_wizard.edit_triggered_by_section_list_id
        await saveAll(currentContext)
      }
    } catch (e) {
      console.warn('[CanvasCompletionWizard] Failed to clear edit_triggered_by_section_list_id', e)
    }

    return
  }

  await initializeCurrentStep()
}

async function canNavigateAway(): Promise<boolean> {
  const iframeWindow = iframeRef.value?.contentWindow as any
  if (currentStep.value?.appSrc && iframeWindow?.canNavigateAway) {
    try {
      const canNavigate = await iframeWindow.canNavigateAway()
      return !!canNavigate
    } catch (e) {
      console.warn('[CanvasCompletionWizard] Error checking canNavigateAway', e)
      return true
    }
  }
  return true
}

function cancel() {
  showCancelConfirmation.value = true
}

function confirmCancel() {
  showCancelConfirmation.value = false
  if (isEditWizard.value) {
    completeWizard()
  } else {
    appEmit('cancelCanvasCompletionWizard')
  }
}

function cancelDialogClose() {
  showCancelConfirmation.value = false
}

async function completeWizard() {
  const currentContext = cloneDeep(activeCanvas.value?.context)
  const updatedContext = merge(currentContext || {}, {
    pitcher: {
      completion_wizard: {
        ...currentContext?.pitcher?.completion_wizard,
        completed: true,
      },
    },
  })

  const meta = merge(cloneDeep(activeCanvas.value?.metadata ?? {}), {
    pitcher: { completion_wizard: { completed: true } },
  })
  await save(updatedContext, meta)
  // Emit event to close wizard and reload canvas
  appEmit('completedCanvasCompletionWizard')
  // Force canvas reload
  window.location.reload()
}

async function initializeWizard() {
  if (activeCanvas.value?.context?.pitcher?.completion_wizard?.initial_section_list_data) {
    return
  }
  const currentContext = cloneDeep(activeCanvas.value?.context)
  const updatedContext = merge(currentContext || {}, {
    pitcher: {
      completion_wizard: {
        completed: false,
        completed_steps: {},
        initial_section_list_data: sectionListNodes.value.reduce(
          (acc, node) => {
            const data = node.data as SectionListProps
            acc[node.id] = data.sections
            return acc
          },
          {} as Record<ComponentNode['id'], SectionListProps['sections']>,
        ),
      },
    },
  })

  const meta = merge(cloneDeep(activeCanvas.value?.metadata ?? {}), {
    pitcher: { completion_wizard: { completed: false } },
  })
  await save(updatedContext, meta)
}

function handleKeydown(event: KeyboardEvent) {
  if (event.key === 'Enter' || event.key === 'Escape') {
    // prevent wizard closing on Enter or Escape key
    event.stopPropagation()
  }
}

onMounted(async () => {
  onWizardEnter.value?.()
  // Restore sidebar collapsed state from localStorage
  const savedCollapsedState = localStorage.getItem(WIZARD_SIDEBAR_COLLAPSED_KEY)
  if (savedCollapsedState !== null) {
    isCollapsed.value = savedCollapsedState === 'true'
  }

  await initializeWizard()

  // Initialize completed steps using current context data only; avoid extra writes
  initializeCompletedSteps()

  mounted.value = true
  await goToEditTriggeredStepIfNeeded()
})

onBeforeUnmount(() => {
  onWizardExit.value?.()
})
</script>
